import React, { useMemo, useState } from "react";
import { toast } from "sonner";
import { Plus, ChevronUp, ChevronDown, Trash2, Pencil, Check, X, Search } from "lucide-react";
import { api, apiErr } from "@/lib/api";
import { Card, Empty, Toggle } from "./ui";

export default function CategoriesModule({ rid, data, reload }) {
  const [cats, setCats] = useState([...data.categories].sort((a, b) => a.order - b.order));
  const [editing, setEditing] = useState(null);
  const [draft, setDraft] = useState({ name: "", subtitle: "" });
  const [q, setQ] = useState("");
  const [filter, setFilter] = useState("all");
  const [delTarget, setDelTarget] = useState(null);

  const countFor = (cid) => data.products.filter((p) => p.category_id === cid).length;
  const refresh = async () => { const { data } = await api.get(`/restaurants/${rid}/full`); setCats([...data.categories].sort((a, b) => a.order - b.order)); reload(); };

  const add = async () => { try { await api.post(`/restaurants/${rid}/categories`, { name: "Nova categoria" }); await refresh(); toast.success("Categoria criada"); } catch (e) { toast.error(apiErr(e)); } };
  const saveEdit = async (c) => { if (!draft.name.trim()) return toast.error("Informe o nome"); try { await api.put(`/restaurants/${rid}/categories/${c.id}`, draft); setEditing(null); await refresh(); toast.success("Alterações salvas"); } catch (e) { toast.error(apiErr(e)); } };
  const toggleActive = async (c) => { try { await api.put(`/restaurants/${rid}/categories/${c.id}`, { active: !c.active }); await refresh(); toast.success(c.active ? "Categoria desativada" : "Categoria ativada"); } catch (e) { toast.error(apiErr(e)); } };

  const tryDelete = (c) => {
    if (countFor(c.id) > 0) setDelTarget(c);
    else confirmDelete(c.id);
  };
  const confirmDelete = async (cid) => { if (!window.confirm("Excluir categoria?")) return; try { await api.delete(`/restaurants/${rid}/categories/${cid}`); await refresh(); toast.success("Categoria excluída"); } catch (e) { toast.error(apiErr(e)); } };

  const move = async (idx, dir) => {
    const arr = [...cats]; const j = idx + dir; if (j < 0 || j >= arr.length) return;
    [arr[idx], arr[j]] = [arr[j], arr[idx]]; setCats(arr);
    try { await api.put(`/restaurants/${rid}/categories-reorder`, { order: arr.map((c) => c.id) }); reload(); } catch (e) { toast.error(apiErr(e)); }
  };

  const visible = useMemo(() => cats.filter((c) => {
    if (filter === "active" && !c.active) return false;
    if (filter === "inactive" && c.active) return false;
    if (q && !c.name.toLowerCase().includes(q.toLowerCase())) return false;
    return true;
  }), [cats, q, filter]);

  return (
    <div className="space-y-4" data-testid="categories-module">
      <div className="flex flex-col sm:flex-row gap-3 sm:items-center">
        <div className="flex-1 flex items-center gap-2 rounded-xl bg-white/[0.04] border border-white/10 px-3 py-2">
          <Search size={16} className="text-zinc-500" />
          <input value={q} onChange={(e) => setQ(e.target.value)} data-testid="cat-search" placeholder="Buscar categoria..." className="flex-1 bg-transparent outline-none text-sm" />
        </div>
        <select value={filter} onChange={(e) => setFilter(e.target.value)} data-testid="cat-filter" className="rounded-xl bg-white/[0.04] border border-white/10 px-3 py-2.5 text-sm outline-none">
          <option value="all" className="bg-zinc-900">Todas</option>
          <option value="active" className="bg-zinc-900">Ativas</option>
          <option value="inactive" className="bg-zinc-900">Inativas</option>
        </select>
        <button onClick={add} data-testid="cat-add" className="flex items-center justify-center gap-2 rounded-full hmmm-gradient px-4 py-2.5 font-bold hmmm-glow text-white"><Plus size={18} /> Nova</button>
      </div>

      {visible.length === 0 && <Empty>Nenhuma categoria encontrada.</Empty>}
      <div className="space-y-2">
        {visible.map((c, idx) => (
          <Card key={c.id} className="!p-3">
            <div className="flex items-center gap-3">
              <div className="flex flex-col">
                <button onClick={() => move(cats.indexOf(c), -1)} disabled={cats.indexOf(c) === 0} data-testid={`cat-up-${c.id}`} className="text-zinc-500 hover:text-white disabled:opacity-30"><ChevronUp size={16} /></button>
                <button onClick={() => move(cats.indexOf(c), 1)} disabled={cats.indexOf(c) === cats.length - 1} data-testid={`cat-down-${c.id}`} className="text-zinc-500 hover:text-white disabled:opacity-30"><ChevronDown size={16} /></button>
              </div>
              {editing === c.id ? (
                <div className="flex-1 flex flex-wrap gap-2">
                  <input value={draft.name} onChange={(e) => setDraft((d) => ({ ...d, name: e.target.value }))} data-testid={`cat-name-${c.id}`} className="flex-1 min-w-[120px] rounded-lg bg-white/[0.04] border border-white/10 px-3 py-2 text-sm outline-none" placeholder="Nome" />
                  <input value={draft.subtitle} onChange={(e) => setDraft((d) => ({ ...d, subtitle: e.target.value }))} className="flex-1 min-w-[120px] rounded-lg bg-white/[0.04] border border-white/10 px-3 py-2 text-sm outline-none" placeholder="Subtítulo (opcional)" />
                  <button onClick={() => saveEdit(c)} data-testid={`cat-savedit-${c.id}`} className="h-9 w-9 rounded-lg hmmm-gradient flex items-center justify-center"><Check size={16} /></button>
                  <button onClick={() => setEditing(null)} className="h-9 w-9 rounded-lg bg-white/10 flex items-center justify-center"><X size={16} /></button>
                </div>
              ) : (
                <>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2">
                      <p className="font-semibold truncate">{c.name}</p>
                      <span className="text-[10px] bg-white/10 px-1.5 py-0.5 rounded-full text-zinc-400">{countFor(c.id)} itens</span>
                      {!c.active && <span className="text-[10px] bg-amber-600/30 text-amber-300 px-1.5 py-0.5 rounded-full">Inativa</span>}
                    </div>
                    {c.subtitle && <p className="text-xs text-zinc-500 truncate">{c.subtitle}</p>}
                  </div>
                  <Toggle checked={c.active} onChange={() => toggleActive(c)} tid={`cat-active-${c.id}`} />
                  <button onClick={() => { setEditing(c.id); setDraft({ name: c.name, subtitle: c.subtitle || "" }); }} data-testid={`cat-edit-${c.id}`} className="h-9 w-9 rounded-lg bg-white/5 hover:bg-white/10 flex items-center justify-center"><Pencil size={15} /></button>
                  <button onClick={() => tryDelete(c)} data-testid={`cat-del-${c.id}`} className="h-9 w-9 rounded-lg bg-white/5 hover:bg-rose-600 flex items-center justify-center"><Trash2 size={15} /></button>
                </>
              )}
            </div>
          </Card>
        ))}
      </div>

      {delTarget && <DeleteCatModal rid={rid} cat={delTarget} cats={cats} count={countFor(delTarget.id)} onClose={() => setDelTarget(null)} onDone={async () => { setDelTarget(null); await refresh(); }} />}
    </div>
  );
}

function DeleteCatModal({ rid, cat, cats, count, onClose, onDone }) {
  const others = cats.filter((c) => c.id !== cat.id);
  const [target, setTarget] = useState(others[0]?.id || "");
  const [loading, setLoading] = useState(false);

  const moveAndDelete = async () => {
    if (!target) return toast.error("Selecione uma categoria de destino");
    setLoading(true);
    try {
      const prods = (await api.get(`/restaurants/${rid}/full`)).data.products.filter((p) => p.category_id === cat.id);
      await Promise.all(prods.map((p) => api.put(`/restaurants/${rid}/products/${p.id}`, { category_id: target })));
      await api.delete(`/restaurants/${rid}/categories/${cat.id}`);
      toast.success("Produtos movidos e categoria excluída");
      onDone();
    } catch (e) { toast.error(apiErr(e)); } finally { setLoading(false); }
  };
  const deleteAll = async () => {
    setLoading(true);
    try { await api.delete(`/restaurants/${rid}/categories/${cat.id}`); toast.success("Categoria e produtos excluídos"); onDone(); }
    catch (e) { toast.error(apiErr(e)); } finally { setLoading(false); }
  };

  return (
    <div className="fixed inset-0 z-[90] bg-black/70 flex items-center justify-center px-4" onClick={onClose}>
      <div className="w-full max-w-md bg-[#141417] border border-white/10 rounded-3xl p-6" onClick={(e) => e.stopPropagation()} data-testid="cat-delete-modal">
        <h3 className="font-head text-lg font-extrabold">Excluir "{cat.name}"</h3>
        <p className="text-sm text-zinc-400 mt-1">Esta categoria possui <b className="text-white">{count} produto(s)</b>. O que deseja fazer?</p>
        {others.length > 0 && (
          <div className="mt-4">
            <label className="text-xs font-semibold text-zinc-400">Mover produtos para</label>
            <select value={target} onChange={(e) => setTarget(e.target.value)} data-testid="cat-move-target" className="w-full mt-1 rounded-xl bg-white/[0.04] border border-white/10 px-3 py-2.5 text-sm outline-none">
              {others.map((c) => <option key={c.id} value={c.id} className="bg-zinc-900">{c.name}</option>)}
            </select>
            <button onClick={moveAndDelete} disabled={loading} data-testid="cat-move-delete" className="w-full mt-3 rounded-full hmmm-gradient py-3 font-bold text-white hmmm-glow disabled:opacity-60">Mover produtos e excluir</button>
          </div>
        )}
        <button onClick={deleteAll} disabled={loading} data-testid="cat-delete-all" className="w-full mt-2 rounded-full bg-rose-600/20 text-rose-300 py-3 font-bold disabled:opacity-60">Excluir categoria e produtos</button>
        <button onClick={onClose} className="w-full mt-2 text-center text-sm text-zinc-400">Cancelar</button>
      </div>
    </div>
  );
}
